import DOMPurify from "dompurify";

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 *
 * @param html - The HTML string to sanitize
 * @param options - Optional configuration for DOMPurify
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export const sanitizeHtml = (
  html: string | null | undefined,
  options?: {
    allowedTags?: string[];
    allowedAttributes?: string[];
    stripTags?: boolean;
  }
): string => {
  if (!html || typeof html !== "string") {
    return "";
  }

  // Default configuration allows common formatting tags but prevents script execution
  const defaultConfig: import("dompurify").Config = {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "span",
      "div",
      "a",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "blockquote",
      "code",
      "pre",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "class", "id"],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ["script", "object", "embed", "iframe", "frame"],
    FORBID_ATTR: [
      "onclick",
      "onload",
      "onerror",
      "onmouseover",
      "onfocus",
      "onblur",
    ],
    USE_PROFILES: { html: true },
  };

  // Override with custom options if provided
  const config: import("dompurify").Config = {
    ...defaultConfig,
    ...(options?.allowedTags && { ALLOWED_TAGS: options.allowedTags }),
    ...(options?.allowedAttributes && {
      ALLOWED_ATTR: options.allowedAttributes,
    }),
    ...(options?.stripTags && { ALLOWED_TAGS: [] }),
  };

  return DOMPurify.sanitize(html, config);
};

/**
 * Sanitizes HTML for form labels - allows links but strips most other tags
 */
export const sanitizeFormLabel = (html: string | null | undefined): string => {
  return sanitizeHtml(html, {
    allowedTags: ["a", "strong", "b", "em", "i", "span"],
    allowedAttributes: ["href", "target", "rel", "class"],
  });
};

/**
 * Sanitizes HTML for notifications - allows basic formatting
 */
export const sanitizeNotification = (
  html: string | null | undefined
): string => {
  return sanitizeHtml(html, {
    allowedTags: ["p", "br", "strong", "b", "em", "i", "span", "a"],
    allowedAttributes: ["href", "target", "rel", "class"],
  });
};

/**
 * Safe CSS properties allowed in document content (Froala editor output).
 * Properties not in this set will be stripped from style attributes.
 */
const SAFE_CSS_PROPERTIES = new Set([
  "color",
  "background-color",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "text-align",
  "text-decoration",
  "text-indent",
  "text-transform",
  "line-height",
  "letter-spacing",
  "word-spacing",
  "white-space",
  "vertical-align",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "border",
  "border-top",
  "border-right",
  "border-bottom",
  "border-left",
  "border-color",
  "border-style",
  "border-width",
  "border-collapse",
  "border-spacing",
  "width",
  "height",
  "min-width",
  "min-height",
  "max-width",
  "max-height",
  "list-style",
  "list-style-type",
  "display",
  "overflow",
  "float",
  "clear",
]);

const SAFE_DISPLAY_VALUES = new Set([
  "inline",
  "block",
  "inline-block",
  "table",
  "table-row",
  "table-cell",
  "none",
]);

const DANGEROUS_CSS_PATTERN =
  /url\s*\(|expression\s*\(|-moz-binding|behavior\s*:|javascript:|@import/i;

/**
 * Sanitizes a CSS style string, keeping only safe properties and values.
 */
function sanitizeStyleValue(style: string): string {
  return style
    .split(";")
    .map((decl) => decl.trim())
    .filter((decl) => {
      if (!decl) return false;
      const colonIdx = decl.indexOf(":");
      if (colonIdx === -1) return false;

      const property = decl.substring(0, colonIdx).trim().toLowerCase();
      const value = decl.substring(colonIdx + 1).trim();

      if (!SAFE_CSS_PROPERTIES.has(property)) return false;
      if (DANGEROUS_CSS_PATTERN.test(value)) return false;
      if (
        property === "display" &&
        !SAFE_DISPLAY_VALUES.has(value.toLowerCase())
      )
        return false;

      return true;
    })
    .join("; ");
}

/**
 * Sanitizes HTML for document content - allows most formatting but prevents script execution.
 * Uses a separate DOMPurify instance with a hook to whitelist safe CSS properties only.
 */
export const sanitizeDocument = (html: string | null | undefined): string => {
  if (!html || typeof html !== "string") {
    return "";
  }

  const purify =
    "default" in DOMPurify && typeof DOMPurify.default === "function"
      ? DOMPurify.default()
      : DOMPurify;

  purify.addHook("uponSanitizeAttribute", (_node, data) => {
    if (data.attrName === "style") {
      const cleaned = sanitizeStyleValue(data.attrValue);
      if (cleaned) {
        data.attrValue = cleaned;
      } else {
        data.keepAttr = false;
      }
    }
  });

  const result = purify.sanitize(html, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "span",
      "div",
      "a",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "blockquote",
      "code",
      "pre",
      "table",
      "thead",
      "tbody",
      "tr",
      "td",
      "th",
      "img",
    ],
    ALLOWED_ATTR: [
      "href",
      "target",
      "rel",
      "class",
      "id",
      "style",
      "src",
      "alt",
      "width",
      "height",
    ],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ["script", "object", "embed", "iframe", "frame"],
    FORBID_ATTR: [
      "onclick",
      "onload",
      "onerror",
      "onmouseover",
      "onfocus",
      "onblur",
    ],
    USE_PROFILES: { html: true },
  });

  purify.removeAllHooks();

  return result;
};

/**
 * Strips all HTML tags - for plain text output
 */
export const stripHtml = (html: string | null | undefined): string => {
  return sanitizeHtml(html, { stripTags: true });
};
